// src/features/auth/pages/ForcePinSetupPage.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { Dialpad as DialpadIcon } from '@mui/icons-material';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import PinKeypad from '../../../components/common/PinKeypad';

export default function ForcePinSetupPage() {
  const { t } = useTranslation(['auth', 'staff', 'common']);
  const { showSuccess } = useSnackbar();
  const { setCurrentStaffPin, dismissCurrentStaffPinPrompt } = useStaff();

  const [currentPassword, setCurrentPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Hard reload (not navigate()) so AuthContext re-fetches the user and
  // show_pin_setup_prompt comes back false — same pattern as the other
  // force-* pages, avoiding a redirect loop back to this page.
  const finishAndRedirect = () => {
    window.location.href = '/home';
  };

  const handleSkip = async () => {
    setLoading(true);
    try {
      await dismissCurrentStaffPinPrompt();
    } finally {
      finishAndRedirect();
    }
  };

  const submitPin = async () => {
    if (loading) return;

    if (!currentPassword) {
      setError(t('common:messages.password_required'));
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError(t('auth:password_dialog.pin_required'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await setCurrentStaffPin(currentPassword, pin);

      if (result.success) {
        showSuccess(t('auth:pin_setup.success'));
        finishAndRedirect();
      } else {
        if (result.error?.error_code === 'PASSWORD__INVALID') {
          setError(t('staff:profile.current_password_invalid'));
        } else {
          setError(result.error || t('staff:profile.pin_set_failed'));
        }
        setPin('');
        setLoading(false);
      }
    } catch {
      setError(t('staff:profile.pin_set_failed'));
      setPin('');
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitPin();
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        py={4}
      >
        <Box textAlign="center" mb={4}>
          <DialpadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('auth:pin_setup.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('auth:pin_setup.description')}
          </Typography>
        </Box>

        <Card sx={{ width: '100%' }}>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('common:labels.password')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                  autoComplete="current-password"
                />

                <Box>
                  <Typography variant="body2" color="text.secondary" textAlign="center" mb={1.5}>
                    {t('auth:pin_setup.pin_label')}
                  </Typography>
                  <PinKeypad
                    value={pin}
                    onChange={setPin}
                    disabled={loading}
                    error={!!error}
                    onComplete={submitPin}
                    onEnter={submitPin}
                  />
                </Box>

                {error && (
                  <Alert severity="error">{error}</Alert>
                )}

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleSkip}
                    disabled={loading}
                  >
                    {t('auth:pin_setup.not_now_button')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <DialpadIcon />}
                  >
                    {loading ? t('common:buttons.saving') : t('auth:pin_setup.set_button')}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}
